import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useBlocker, useParams } from 'react-router'
import {
  FileIcon,
  TopBar,
  AppLayout,
  useGetPlanByIdQuery,
  useGetTaskByIdQuery,
  useGetTaskSampleSummaryQuery,
  useGetTaskDelegationQuery,
  ModalDialog,
  Mtds,
  getParentBreadcrumbs,
  AnalysisTypeApprovalStatusTag,
  getTaskHierarchyIds,
  flattenTasks,
  getTaskDetailProperties,
  getTaskHeaderProperties,
  MarkdownCard,
  TaskScreenContent,
  SampleShippingAddress,
  type UpdateTask,
  useGetContractsQuery,
  useGetOkProgramsQuery,
  useGetPurposesQuery,
  useGetSubPurposesQuery,
  useGetSampntTypes,
  useGetBatchDataQuery,
  useGetOwnerNameQuery,
  PlanStatusTag,
  type Task,
  useUpdateTaskMutation,
  type ScreenHeaderProperty
} from 'prove-common'
import TaskDelegationTab from '../components/task-delegation-tab/TaskDelegationTab'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { AppLogo } from '../components/app-logo/AppLogo'
import { TaskDuedateTab } from '../components/task-duedata-tab/TaskDuedateTab'
import { TaskActionsMenu } from '../components/task-actions-menu/TaskActionsMenu'
import { PlanStatCard } from '../components/plan-stat-card/PlanStatCard'
import { EditableMatrixAmountField } from '../components/editable-matrix-amount-field/EditableMatrixAmountField'
import { EditableQuantityField } from '../components/editable-quantity-field/EditableQuantityField'
import { EditableSubPurposeField } from '../components/editable-sub-purpose-field/EditableSubPurposeField'
import { EditableSampntTypeField } from '../components/editable-sampnt-type-field/EditableSampntTypeField'
import { doesTaskBelongToPlan } from '../workspaces/workspaces'
import { EditableBooleanField } from '../components/editable-boolean-field/EditableBooleanField'
import { EditableArchiveCategoryField } from '../components/editable-archive-category-field/EditableArchiveCategoryField'
import { useTaskReceptionAddress } from '../hooks/useTaskReceptionAddress'

const getApprovalUi = (
  task: Task | undefined,
  isLoadingContracts: boolean
): {
  approvalStatusValue: React.ReactNode
  planStatusValue: React.ReactNode
  approvalAlert: React.ReactNode
} => {
  if (isLoadingContracts) {
    return {
      approvalStatusValue: <Mtds.Skeleton />,
      planStatusValue: undefined,
      approvalAlert: null
    }
  }

  if (task?.analysisTypeApprovalStatus == null) {
    return {
      approvalStatusValue: '-',
      planStatusValue: undefined,
      approvalAlert: null
    }
  }

  const approvalStatusValue = (
    <AnalysisTypeApprovalStatusTag
      status={task.analysisTypeApprovalStatus}
      approvalComment={task.approvalComment}
    />
  )
  const planStatusValue =
    task.analysisTypeApprovalStatus !== 'approved'
      ? approvalStatusValue
      : undefined

  return {
    approvalStatusValue,
    planStatusValue,
    approvalAlert: getApprovalAlert(task)
  }
}

const getApprovalAlert = (task: Task): React.ReactNode => {
  if (task.analysisTypeApprovalStatus === 'declined') {
    return (
      <Mtds.Alert data-color={'danger'}>
        <Mtds.Heading>
          Denne kombinasjonen er avvist av laboratoriet
        </Mtds.Heading>
        <p>{task.approvalComment ?? 'Ingen avvisningsgrunn oppgitt.'}</p>
      </Mtds.Alert>
    )
  }

  if (task.analysisTypeApprovalStatus === 'awaiting') {
    return (
      <Mtds.Alert data-color={'warning'}>
        <Mtds.Heading>
          Denne kombinasjonen avventer godkjenning fra laboratoriet
        </Mtds.Heading>
      </Mtds.Alert>
    )
  }

  return null
}

export default function TaskScreen() {
  const queryClient = useQueryClient()
  const [isDescriptionDirty, setIsDescriptionDirty] = useState(false)
  const [isDelegationDirty, setIsDelegationDirty] = useState(false)

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDescriptionDirty ||
      (isDelegationDirty && currentLocation.pathname !== nextLocation.pathname)
  )

  const dialogOpen = blocker.state === 'blocked'

  const handleDiscard = () => {
    blocker.proceed?.()
  }

  const handleKeepEditing = () => {
    blocker.reset?.()
  }
  const { planId, taskId } = useParams()
  const {
    data: plan,
    isPending: isPendingPlan,
    isError: isErrorPlan,
    isSuccess: isSuccessPlan
  } = useGetPlanByIdQuery(Number(planId))
  const {
    data: task,
    isPending: isPendingTask,
    isError: isErrorTask,
    isSuccess: isSuccessTask,
    refetch
  } = useGetTaskByIdQuery(Number(taskId))
  const {
    data: sampleSummary,
    isPending: isPendingSampleSummary,
    isError: isErrorSampleSummary
  } = useGetTaskSampleSummaryQuery(Number(taskId))
  const {
    data: taskDelegationData,
    isPending: isPendingTaskDelegation,
    refetch: refetchDelegation
  } = useGetTaskDelegationQuery(Number(taskId))
  const {
    data: purposes,
    isPending: isPendingPurposes,
    isError: isErrorPurposes
  } = useGetPurposesQuery()
  const purpose = purposes?.find((r) => r.id === task?.purposeId)
  const purposeText = purpose?.name
  const { data: okPrograms } = useGetOkProgramsQuery()
  const okProgram = okPrograms?.find((r) => r.id === plan?.okProgramId)
  const okProgramValue = okProgram ? (
    <>
      <strong>{okProgram.number}</strong> {okProgram.name}
    </>
  ) : undefined
  const { data: subPurposes } = useGetSubPurposesQuery()
  const { data: sampntTypes } = useGetSampntTypes()
  const { data: batchData } = useGetBatchDataQuery()
  const archiveCategories = batchData?.ordningsverdier ?? []
  const { data: ownerName } = useGetOwnerNameQuery(task?.ownerId ?? undefined)
  const { data: contracts, isLoading: isLoadingContracts } =
    useGetContractsQuery()
  const updateTask = useUpdateTaskMutation()
  const receptionAddress = useTaskReceptionAddress(
    task?.lab?.id,
    task?.labReceptionAddressId
  )
  const selectedReceptionAddress = receptionAddress.data?.find(
    (address) => address.id === task?.labReceptionAddressId
  )
  const isParentTask = task?.parentTaskId == null
  const breadcrumbItems = [
    {
      label: 'Planer',
      to: '/plans'
    },
    ...(plan ? getParentBreadcrumbs(plan) : []),
    {
      label: plan?.description ?? '',
      to: `/plans/${planId}`
    },
    {
      label: task?.analysisType?.sampleType?.matrix,
      icon: <FileIcon />
    }
  ]
  const isLoading =
    isPendingPlan ||
    isPendingTask ||
    isPendingPurposes ||
    isPendingSampleSummary ||
    isPendingTaskDelegation
  const hasTaskPlanMismatch =
    task != null &&
    planId != null &&
    !doesTaskBelongToPlan(task, Number(planId))
  const isError =
    isErrorPlan ||
    isErrorTask ||
    isErrorPurposes ||
    isErrorSampleSummary ||
    hasTaskPlanMismatch
  const schema = task?.analysisType?.payloadSchema
  const canEditTask = task?.canManage === true
  const canEditQuantity = canEditTask
  const archiveCategoriesWithFallback =
    task?.ordningsVerdi != null &&
    !archiveCategories.some(
      (category) => category.id === task.ordningsVerdi?.id
    )
      ? [task.ordningsVerdi, ...archiveCategories]
      : archiveCategories
  const canEditArchiveCategory = canEditTask && batchData != null
  const sampleTaskIds = task
    ? getTaskHierarchyIds(task.id, taskDelegationData?.delegation)
    : []
  const sampleTaskQuery = sampleTaskIds.map((id) => `taskIds=${id}`).join('&')
  const { approvalStatusValue, planStatusValue, approvalAlert } = getApprovalUi(
    task,
    isLoadingContracts
  )
  const contract = contracts?.find((item) => item.id === task?.contractId)
  const contractValue = isLoadingContracts ? (
    <Mtds.Skeleton />
  ) : contract ? (
    <Link to={`/contracts/${contract.id}`}>{contract.title}</Link>
  ) : (
    '-'
  )
  let headerProperties: ScreenHeaderProperty[] = []
  let detailProperties: ScreenHeaderProperty[] = []

  if (task) {
    headerProperties = getTaskHeaderProperties({
      task,
      statusValue:
        planStatusValue ??
        (task.planStatus ? (
          <PlanStatusTag status={task.planStatus} />
        ) : undefined),
      sampntTypeValue: (
        <EditableSampntTypeField
          value={task.sampntType?.id}
          sampntTypes={sampntTypes ?? []}
          canEdit={canEditTask && receptionAddress.canSave}
          isSaving={updateTask.isPending || !receptionAddress.canSave}
          onSave={handleSaveSampntType}
        />
      ),
      duedatesValue: <TaskDuedateTab task={task} />,
      matrixAmountValue: (
        <EditableMatrixAmountField
          value={task.matrixAmount}
          canEdit={canEditTask && receptionAddress.canSave}
          isSaving={updateTask.isPending || !receptionAddress.canSave}
          onSave={(matrixAmount) =>
            saveTask({ matrixAmount }, 'Mengden er oppdatert')
          }
        />
      )
    }).filter((property) => property.label !== 'Prøver tatt')
    detailProperties = getTaskDetailProperties({
      task,
      planValue: plan?.description,
      planLinkTo: `/plans/${planId}`,
      contractValue,
      approvalStatusValue,
      quantityValue: (
        <EditableQuantityField
          value={task.quantity ?? 0}
          canEdit={canEditQuantity && receptionAddress.canSave}
          isSaving={updateTask.isPending || !receptionAddress.canSave}
          onSave={handleSaveQuantity}
        />
      ),
      countSubSamplesValue: (
        <EditableBooleanField
          value={task.countSubSamples ?? false}
          label='Hvordan telle prøver'
          trueLabel='Delprøver'
          falseLabel='Prøveposer'
          canEdit={canEditTask && receptionAddress.canSave}
          isSaving={updateTask.isPending || !receptionAddress.canSave}
          onSave={(value) => handleSaveTask({ countSubSamples: value })}
        />
      ),
      locationRequiredValue: (
        <EditableBooleanField
          value={task.locationRequired ?? false}
          label='Krever nøyaktig posisjon'
          canEdit={canEditTask && receptionAddress.canSave}
          isSaving={updateTask.isPending || !receptionAddress.canSave}
          onSave={(value) => handleSaveTask({ locationRequired: value })}
        />
      ),
      archiveCategoryValue: (
        <EditableArchiveCategoryField
          value={task.ordningsVerdi?.id}
          archiveCategories={archiveCategoriesWithFallback}
          canEdit={canEditArchiveCategory && receptionAddress.canSave}
          isSaving={updateTask.isPending || !receptionAddress.canSave}
          onSave={(value) =>
            handleSaveTask({ ordningsverdiId: value }, 'Arkivkategori lagret')
          }
        />
      ),
      purposeValue: purposeText ?? '-',
      okProgramValue: okProgramValue ?? '-',
      subPurposeValue: (
        <EditableSubPurposeField
          value={task.subPurposeId}
          subPurposes={subPurposes ?? []}
          canEdit={canEditTask && receptionAddress.canSave}
          isSaving={updateTask.isPending || !receptionAddress.canSave}
          onSave={handleSaveSubPurpose}
        />
      ),
      ownerValue: ownerName?.name ?? '-',
      schemaValue: schema ? (
        <Link to={`/contracts/${task.contractId}/schemas/${schema.id}`}>
          {schema.name}
        </Link>
      ) : (
        '-'
      )
    })
  }

  async function handleSaveTask(
    changes: Partial<UpdateTask>,
    message?: string
  ): Promise<void> {
    await saveTask(changes, message)
  }

  async function saveTask(
    changes: Partial<UpdateTask>,
    message = 'Oppgaven er oppdatert'
  ): Promise<boolean> {
    if (!receptionAddress.canSave) {
      Mtds.toast.warning(receptionAddress.saveBlockedMessage)
      return false
    }
    if (task?.etag == null || task.analysisType?.id == null) {
      Mtds.toast.danger('Kan ikke lagre: oppgavedata mangler')
      return false
    }

    const body: UpdateTask = {
      quantity: task.quantity ?? 0,
      countSubSamples: task.countSubSamples,
      sampntTypeId: task.sampntType?.id,
      labId: task.lab?.id,
      labReceptionAddressId: task.labReceptionAddressId,
      description: task.description,
      ownerId: task.ownerId,
      analysisTypeId: task.analysisType.id,
      planStatus: task.planStatus,
      ordningsverdiId: task.ordningsVerdi?.id,
      locationRequired: task.locationRequired,
      contractId: task.contractId,
      duedates: task.duedates,
      subPurposeId: task.subPurposeId,
      matrixAmount: task.matrixAmount ?? null,
      ...changes
    }

    try {
      await updateTask.mutateAsync({ taskId: task.id, etag: task.etag, body })
      await Promise.all([
        refetch(),
        refetchDelegation(),
        queryClient.invalidateQueries({
          queryKey: ['tasks', 'proveplan-overview']
        }),
        queryClient.invalidateQueries({ queryKey: ['plans'] })
      ])
      Mtds.toast.success(message)
      return true
    } catch {
      Mtds.toast.danger('Kunne ikke lagre oppgaven')
      return false
    }
  }

  async function handleSaveQuantity(quantity: number) {
    await handleSaveTask({ quantity }, 'Prøveantallet er oppdatert')
  }

  async function handleSaveSubPurpose(subPurposeId?: number) {
    await handleSaveTask({ subPurposeId }, 'Underhensikt lagret')
  }

  async function handleSaveSampntType(sampntTypeId: number) {
    await handleSaveTask({ sampntTypeId }, 'Uttakssted lagret')
  }

  const handleSaveDescription = async (description: string) => {
    await handleSaveTask({ description }, 'Beskrivelse lagret')
  }

  return (
    <AppLayout
      useMobileBar={false}
      sidebar={<SideBarLayout />}
      isLoading={isLoading}
      isError={isError}
      topBar={
        <TopBar
          logo={<AppLogo />}
          breadcrumbItems={breadcrumbItems}
          showNotificationBell={true}
        />
      }
      isFullWidth
    >
      {!isParentTask && (
        <Mtds.Alert data-color={'danger'}>
          <Mtds.Heading>Visningen støttes ikke</Mtds.Heading>
          <p>
            Denne visningen er kun tilgjengelig for hovedoppgaver (øverst i
            hierarkiet)
          </p>
        </Mtds.Alert>
      )}
      {isSuccessTask && isSuccessPlan && isParentTask && sampleSummary && (
        <TaskScreenContent
          labDetails={
            task.labReceptionAddressId != null &&
            task.lab != null &&
            receptionAddress.isPending ? (
              <Mtds.Muted role='status'>Laster mottaksadresse...</Mtds.Muted>
            ) : task.labReceptionAddressId != null &&
              receptionAddress.isError ? (
              <Mtds.Muted role='alert'>
                Kunne ikke laste mottaksadressen.
              </Mtds.Muted>
            ) : selectedReceptionAddress ? (
              <SampleShippingAddress
                address={selectedReceptionAddress}
                showName={false}
              />
            ) : null
          }
          title={task.analysisType?.sampleType?.matrix ?? 'Oppgave'}
          actions={<TaskActionsMenu task={task} plan={plan} />}
          headerProperties={headerProperties}
          detailProperties={detailProperties}
          mainMaxWidth={1000}
        >
          {approvalAlert}
          {!receptionAddress.canSave && !receptionAddress.isPending && (
            <Mtds.Alert data-color='warning'>
              {receptionAddress.saveBlockedMessage}
            </Mtds.Alert>
          )}
          {task.quantity != null && (
            <Mtds.Grid data-gap='4' data-items='300'>
              <PlanStatCard
                color='success'
                label='Prøver tatt'
                value={task.totalCount ?? 0}
                description={`av ${task.quantity} prøver planlagt`}
                progressValue={task.totalCount ?? 0}
                progressMax={task.quantity}
                to={`/samples?${sampleTaskQuery}&tabIndex=0`}
              />
              <PlanStatCard
                color='danger'
                label='Prøver påvist'
                value={sampleSummary.positiveSampleCount}
                description={`av ${sampleSummary.totalSampleCount} prøver registrert`}
                badge={
                  <Mtds.Tag
                    data-color='danger'
                    data-icon='none'
                    className='rounded-full!'
                  >
                    {(sampleSummary.totalSampleCount === 0
                      ? 0
                      : (sampleSummary.positiveSampleCount /
                          sampleSummary.totalSampleCount) *
                        100
                    ).toLocaleString('nb-NO', {
                      minimumFractionDigits:
                        sampleSummary.positiveSampleCount === 0 ? 0 : 2,
                      maximumFractionDigits: 2
                    })}{' '}
                    %
                  </Mtds.Tag>
                }
                progressValue={sampleSummary.positiveSampleCount}
                progressMax={sampleSummary.totalSampleCount}
                to={`/samples?${flattenTasks([task])
                  .map(({ id }) => `taskIds=${id}`)
                  .join('&')}&tabIndex=0&result=POSITIVE`}
              />
            </Mtds.Grid>
          )}
          <MarkdownCard
            title='Beskrivelse'
            value={task.description ?? ''}
            emptyText='Ingen beskrivelse.'
            maxLength={2000}
            editAriaLabel='Rediger beskrivelse'
            placeholder='Skriv en beskrivelse...'
            onSave={
              canEditTask && receptionAddress.canSave
                ? handleSaveDescription
                : undefined
            }
            isSaving={updateTask.isPending || !receptionAddress.canSave}
            onDirtyChange={setIsDescriptionDirty}
          />
          <Mtds.Divider data-gap={1} />
          <TaskDelegationTab
            task={task}
            plan={plan}
            refetchTask={refetch}
            onDirtyChange={setIsDelegationDirty}
          />
        </TaskScreenContent>
      )}
      <ModalDialog
        id='unsaved-description-dialog'
        closedby='closerequest'
        open={dialogOpen}
      >
        <Mtds.Button
          command='close'
          commandfor='unsaved-description-dialog'
          aria-label='Lukk'
          onClick={handleKeepEditing}
        />
        <Mtds.Prose>
          <Mtds.Heading>Forkast endringer?</Mtds.Heading>
          <p>
            Du har ulagrede endringer i{' '}
            {isDescriptionDirty && isDelegationDirty
              ? 'beskrivelsen og fordelingen'
              : isDelegationDirty
                ? 'fordelingen'
                : 'beskrivelsen'}
            . Vil du forkaste dem?
          </p>
          <Mtds.Flex style={{ paddingTop: 'var(--mtds-8)' }}>
            <Mtds.Button type='button' onClick={handleKeepEditing}>
              Tilbake til redigering
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              type='button'
              onClick={handleDiscard}
            >
              Forkast endringer
            </Mtds.Button>
          </Mtds.Flex>
        </Mtds.Prose>
      </ModalDialog>
    </AppLayout>
  )
}
